"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import RippleCtaButtonComponent from "@/components/ui/RippleCtaButtonComponent";
import type { CtaComponentProps } from "@/cms/models";

interface IslandState {
  open: string | null;
  interest: string | null;
  showDetails: (key: string) => void;
  closeDetails: () => void;
  /** From the details dialog that dialog closes first, so only one dialog is open at a time. */
  showInterest: (key: string) => void;
  closeInterest: () => void;
}
const Island = createContext<IslandState>({
  open: null,
  interest: null,
  showDetails: () => {},
  closeDetails: () => {},
  showInterest: () => {},
  closeInterest: () => {},
});
export function useListingIsland() {
  return useContext(Island);
}

const triggerOf = (key: string) =>
  document.querySelector<HTMLElement>(`[data-ml-trigger="${key}"]`);
const clearHash = (key: string) => {
  if (window.location.hash === `#${key}`)
    window.history.replaceState(
      window.history.state,
      "",
      window.location.pathname + window.location.search,
    );
};

/** One per section: which listing's details or interest dialog is open, kept in the address as `#bolig-…`. A dialog
 *  opened without a trigger (from the address, or Vis interesse from the details) returns focus to the card's
 *  trigger when it closes; otherwise headless UI returns it to the button that opened it. */
export default function ManualListingsIsland({
  keys,
  children,
}: {
  keys: string[];
  children: ReactNode;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const [interest, setInterest] = useState<string | null>(null);
  const current = useRef<{ open: string | null; interest: string | null }>({
    open: null,
    interest: null,
  });
  const restore = useRef<string | null>(null);
  current.current = { open, interest };
  const known = keys.join(" ");
  useEffect(() => {
    const list = known.split(" ");
    const read = () => {
      const key = decodeURIComponent(window.location.hash.slice(1));
      if (!key || !list.includes(key) || current.current.open === key) return;
      restore.current = key;
      setOpen(key);
    };
    read();
    window.addEventListener("hashchange", read);
    return () => window.removeEventListener("hashchange", read);
  }, [known]);
  const refocus = useCallback((key: string) => {
    if (restore.current !== key) return;
    restore.current = null;
    window.setTimeout(() => triggerOf(key)?.focus(), 0);
  }, []);
  const value = useMemo<IslandState>(
    () => ({
      open,
      interest,
      showDetails: (key) => {
        setOpen(key);
        window.history.replaceState(window.history.state, "", `#${key}`);
      },
      closeDetails: () => {
        const key = current.current.open;
        setOpen(null);
        if (!key) return;
        clearHash(key);
        refocus(key);
      },
      showInterest: (key) => {
        const details = current.current.open;
        if (details) {
          setOpen(null);
          clearHash(details);
          restore.current = key;
        }
        setInterest(key);
      },
      closeInterest: () => {
        const key = current.current.interest;
        setInterest(null);
        if (key) refocus(key);
      },
    }),
    [open, interest, refocus],
  );
  return <Island.Provider value={value}>{children}</Island.Provider>;
}

/** The card's title as the button that opens its details or Vis interesse; a pseudo-element stretches it over the card
 *  (purpose-sections.css), so the card has one trigger and nothing interactive nested in another. */
export function ListingTrigger({
  listingKey,
  mode,
  children,
}: {
  listingKey: string;
  mode: "details" | "interest";
  children: ReactNode;
}) {
  const island = useListingIsland();
  return (
    <button
      className="pf-ml-trigger"
      type="button"
      aria-haspopup="dialog"
      data-ml-trigger={listingKey}
      data-ml-interest={mode === "interest" ? listingKey : undefined}
      onClick={() =>
        mode === "interest"
          ? island.showInterest(listingKey)
          : island.showDetails(listingKey)
      }
    >
      {children}
    </button>
  );
}

/** A card or dialog button that opens the details or Vis interesse, drawn as the section's buttons. */
export function ListingActionButton({
  id,
  listingKey,
  mode,
  className,
  config,
}: {
  id: string;
  listingKey: string;
  mode: "details" | "interest";
  className: string;
  config: CtaComponentProps;
}) {
  const island = useListingIsland();
  return (
    <span className={className}>
      <RippleCtaButtonComponent
        id={id}
        itemIndex={0}
        config={config}
        aria-haspopup="dialog"
        onClick={() =>
          mode === "interest"
            ? island.showInterest(listingKey)
            : island.showDetails(listingKey)
        }
      />
    </span>
  );
}

/** A plain button that opens the details or Vis interesse, for Fokus's own button and the map's popover. */
export function ListingPlainButton({
  listingKey,
  mode,
  className,
  children,
}: {
  listingKey: string;
  mode: "details" | "interest";
  className: string;
  children: ReactNode;
}) {
  const island = useListingIsland();
  return (
    <button
      type="button"
      className={className}
      aria-haspopup="dialog"
      onClick={() =>
        mode === "interest"
          ? island.showInterest(listingKey)
          : island.showDetails(listingKey)
      }
    >
      {children}
    </button>
  );
}
